import { isWeb } from 'affiny-core/helpers/targetCheck/targetCheck';
import merge from 'lodash/merge';
import { EEnvCountry, EEnvLocale, EEnvPlatform } from './env.types';
import { FLAGS } from './helpers/featureToggle/featureToggleFlags';
import { setMetaInformations } from './helpers/html';
import { EReflistChapterName } from './modules/api/repositories/reflists/reflists.types';
import { getSupportedCountryCodeOrFallback } from './services/country';
import { localesService } from './services/locales';
import { i18n } from './translations';
import { DeepPartial } from './types';
import { EBrand as EBrandType, EBrandCode } from './types/env.types';

export enum EAppEnv {
  test = 'test',
  local = 'local',
  staging = 'staging',
  kuberecette = 'kuberecette',
  preprod = 'preprod',
  prod = 'prod',
}

export enum ENodeEnv {
  development = 'development',
  test = 'test',
  production = 'production',
}

export const EXClientLocale = {
  FR: 'fr_FR',
  BE: 'fr_BE',
  CHF: 'fr_CH',
  CHG: 'de_CH',
  EN: 'en_GB',
  IT: 'it_IT',
  ES: 'es_ES',
  DE: 'de_DE',
  DK: 'da_DK',
  NO: 'nb_NO',
  PT: 'pt_PT',
  NL: 'nl_NL',
  SE: 'sv_SE',
  FI: 'fi_FI',
  IE: 'en_IE',
};

export const ELocale = {
  fr: 'fr_FR',
  be: 'fr_BE',
  ch: 'fr_CH',
  it: 'it_IT',
  es: 'es_ES',
  de: 'de_DE',
  dk: 'da_DK',
  no: 'nb_NO',
  pt: 'pt_PT',
  nl: 'nl_NL',
  se: 'sv_SE',
  fi: 'fi_FI',
  ie: 'en_IE',
  en: 'en_GB',
};

export const EDomain = {
  FR: 'https://www.affiny.fr',
  EN: 'https://www.affiny.co.uk',
  IT: 'https://www.meeticaffinity.it',
  ES: 'https://www.meeticaffinity.es',
  PT: 'https://www.meeticaffinity.pt',
  BE: 'https://fr.meeticaffinity.be',
  CHF: 'https://fr.meeticaffinity.ch',
  CHG: 'https://www.partner.ch',
  DE: 'https://www.partner.de',
  DK: 'https://www.matchaffinity.dk',
  NO: 'https://www.matchaffinity.no',
  SE: 'https://www.matchaffinity.se',
  FI: 'https://www.matchaffinity.fi',
  IE: 'https://www.matchaffinity.ie',
  NL: 'https://www.lexamore.nl',
};

export const EBrand = {
  FR: EBrandCode.AFF,
  EN: EBrandCode.AFF,
  IT: EBrandCode.MAF,
  ES: EBrandCode.MAF,
  PT: EBrandCode.MAF,
  BE: EBrandCode.MAF,
  CHF: EBrandCode.MAF,
  CHG: EBrandCode.PA,
  DE: EBrandCode.PA,
  DK: EBrandCode.MA,
  NO: EBrandCode.MA,
  SE: EBrandCode.MA,
  FI: EBrandCode.MA,
  IE: EBrandCode.MA,
  NL: EBrandCode.LE,
};

export const PATH_TO_FORGOTTEN_PASSWORD =
  'unlogged/landing/app/#/forgottenpassword';

enum EPaymentReturnUrl {
  KO = 'affiny://affiny/payment/KO',
  OK = 'affiny://affiny/payment/OK',
}

interface IPaymentReturnUrl {
  KO: string;
  OK: string;
}

interface IFastForwardToggling {
  disabled: boolean;
  chapterLabel?: EReflistChapterName;
  questionIndex?: number;
  pageAction?: string;
}

export interface IConfig {
  envName: string;
  version?: string;
  locale: EEnvLocale;
  platform: EEnvPlatform;
  origin?: string;
  meeticApi: {
    baseApiHostname: string;
    baseApiPath: string;
    websocketUrl: string;
    xClientId: string;
  };
  meeticUrls: {
    cguUrl: string;
    cookiesUseUrl: string;
    privacyUrl: string;
    profileVisibility: string;
    partnerships: string;
    sensitiveDataUrl: string;
    messagesSecurityUrl: string;
    missionUrl: string;
    guideLinesUrl: string;
    paymentReturnUrl: IPaymentReturnUrl;
    paymentMediatorUrl: string;
  };
  credentials: {
    sentryDSN: string;
    trackId: string;
    adjustId: string;
    algoliaApiKey: string;
    algoliaAppId: string;
  };
  fastForward?: IFastForwardToggling;
  featureToggling: Partial<Record<FLAGS, boolean>>;
  defaultNationalityIds: number[];
  // Web Only
  cookies?: {
    oneTrustUrl: string;
    domains: string[];
    defaultDomain: string;
  };
  publicBasePath?: string;
  googleTagManagerContainerIdForOneTrust?: string;
  googleTagManagerContainerIdForGoogleAnalytics?: string;
}

export interface IInputEnv {
  ENV: EAppEnv;
  NODE_ENV?: ENodeEnv; // Setup by create react app
  VERSION?: string; // Setup by Webpack, imported from package.json

  REACT_APP_SENTRY_CONFIG: string;
  /**
   * Defaults nationality ids used in the nationality question of the PC
   */
  REACT_APP_DEFAULT_NATIONALITY_IDS: [number, number, number];
  REACT_APP_CGU_URL: string;
  REACT_APP_COOKIES_USE_URL: string;
  REACT_APP_PRIVACY_URL: string;
  REACT_APP_SENSITIVE_DATA_URL: string;
  REACT_APP_MESSAGES_SECURITY?: string;
  REACT_APP_MISSION_URL: string;
  REACT_APP_GUIDELINES_URL: string;
  REACT_APP_PROFILE_VISIBILITY: string;
  REACT_APP_PARTNERSHIP: string;
  REACT_APP_PAYMENT_MEDIATOR_URL: string;
  REACT_APP_WEBSOCKET_URL: string;
  REACT_APP_TRACK_ID: string;
  REACT_APP_ADJUST_KEY: string;
  /* Algolia */
  REACT_APP_ALGOLIA_API_KEY: string;
  REACT_APP_ALGOLIA_APP_ID: string;
  /* Feature Toggle */
  REACT_APP_DISABLE_FAST_FORWARD?: string;
  REACT_APP_LOGIN_DEBUG?: string;
  /* OneTrust */
  //REACT_APP_ONETRUST_APP_ID_ANDROID: string;
  //REACT_APP_ONETRUST_APP_ID_IOS: string;
}

export interface IConfigStrategy {
  getPlatform(): EEnvPlatform;
  getCountry(): EEnvCountry | string;
  getLocale(): EEnvLocale;
  getBrand?(): EBrandType;
  resetCachedCountry(): Promise<boolean>;
  getConfigOverrides(): DeepPartial<IConfig>;
}

export class Config {
  public get configStrategy(): IConfigStrategy {
    if (!this.pConfigStrategy) {
      throw new Error(
        'affiny-core/env.ts you cannot call .strategy before configure it. Search Config.setConfigStrategy() method'
      );
    }

    return this.pConfigStrategy;
  }

  public get env() {
    if (!this.pConfig) {
      throw new Error(
        'affiny-core/env.ts you cannot call .env before configure it. Search Config.configure() method'
      );
    }

    return this.pConfig;
  }

  private pConfig: IConfig;

  private pConfigStrategy: IConfigStrategy;

  public configure = (value: IInputEnv) => {
    const isWebApp = isWeb();
    const addDomain = (path?: string) => {
      return (
        domain + path + '?styled=2' + (isWebApp ? '' : '&CMP_DISABLED=yes')
      );
    };

    const platform = this.configStrategy.getPlatform();
    const locale = this.configStrategy.getLocale();
    const country = this.getCountry();
    const domain = EDomain[country];
    const brandCode = EBrand[country];

    // With WAM, we start without translations and we get ONLY the right lang.
    // With APP, we start always with all lang.
    localesService.setTranslations(locale);

    if (isWebApp) {
      setMetaInformations(locale, brandCode);
    }

    i18n.setLocale(locale as EEnvLocale);

    const envConfig: IConfig = {
      version: value.VERSION,
      envName: value.ENV,
      platform,
      locale: EEnvLocale[locale],
      meeticApi: {
        baseApiHostname: EDomain[country],
        baseApiPath: '/apimm',
        websocketUrl: value.REACT_APP_WEBSOCKET_URL,
        xClientId: this.getClientId({ platform, country, brandCode }),
      },
      meeticUrls: {
        cguUrl: addDomain(value.REACT_APP_CGU_URL),
        cookiesUseUrl: addDomain(value.REACT_APP_COOKIES_USE_URL),
        privacyUrl: addDomain(value.REACT_APP_PRIVACY_URL),
        sensitiveDataUrl: addDomain(value.REACT_APP_SENSITIVE_DATA_URL),
        messagesSecurityUrl: addDomain(value.REACT_APP_MESSAGES_SECURITY),
        missionUrl: addDomain(value.REACT_APP_MISSION_URL),
        guideLinesUrl: addDomain(value.REACT_APP_GUIDELINES_URL),
        profileVisibility: addDomain(value.REACT_APP_PROFILE_VISIBILITY),
        partnerships: addDomain(value.REACT_APP_PARTNERSHIP),
        paymentReturnUrl: EPaymentReturnUrl,
        paymentMediatorUrl: value.REACT_APP_PAYMENT_MEDIATOR_URL,
      },
      credentials: {
        sentryDSN: value.REACT_APP_SENTRY_CONFIG,
        trackId: value.REACT_APP_TRACK_ID,
        adjustId: value.REACT_APP_ADJUST_KEY,
        algoliaApiKey: value.REACT_APP_ALGOLIA_API_KEY,
        algoliaAppId: value.REACT_APP_ALGOLIA_APP_ID,
      },
      featureToggling: {},
      defaultNationalityIds: value.REACT_APP_DEFAULT_NATIONALITY_IDS,
    };
    const configOverrides = this.configStrategy.getConfigOverrides();

    this.pConfig = merge({}, envConfig, configOverrides);
  };

  public setConfigStrategy = (strategy: IConfigStrategy) => {
    this.pConfigStrategy = strategy;
  };

  private getCountry(): EEnvCountry {
    return getSupportedCountryCodeOrFallback(this.configStrategy.getCountry());
  }

  private getClientId = ({
    platform,
    country,
    brandCode,
  }: {
    platform: EEnvPlatform;
    country: EEnvCountry;
    brandCode: EBrandCode;
  }) => {
    return `${platform}.${brandCode}.${EXClientLocale[country]}`;
  };
}

export const config = new Config();
